import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { http, SIZES } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const SORTS = [
  { key: "popularity", label: "Popularity" },
  { key: "price_asc", label: "Price -- Low to High" },
  { key: "price_desc", label: "Price -- High to Low" },
  { key: "discount", label: "Discount" },
];

const PRICE_BUCKETS = [
  { label: "Under ₹1,000", min: 0, max: 999 },
  { label: "₹1,000 – ₹1,999", min: 1000, max: 1999 },
  { label: "₹2,000 – ₹2,999", min: 2000, max: 2999 },
  { label: "₹3,000 & above", min: 3000, max: Infinity },
];

const Check = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2.5 py-1.5 cursor-pointer text-sm text-[#212121] hover:text-[#C62828]">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 accent-[#C62828] cursor-pointer"
      data-testid={testid}
    />
    {label}
  </label>
);

const CategoryPage = () => {
  const { name } = useParams();
  const [params] = useSearchParams();
  const q = params.get("q");
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("popularity");
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [prices, setPrices] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const title = q ? `Results for "${q}"` : name;

  useSeo({
    title: q ? `${title} | BharatiCrafts` : `${name} — Handcrafted ${name} Online | BharatiCrafts`,
    description: q
      ? `Shop handcrafted products matching "${q}" at BharatiCrafts. Free delivery, Cash on Delivery available.`
      : `Buy authentic handcrafted ${name} online at BharatiCrafts. Free delivery on all orders, Cash on Delivery available.`,
  });

  useEffect(() => {
    const sub = params.get("sub");
    setSubs(sub ? [sub] : []);
    setSizes([]);
    setPrices([]);
  }, [name, q, params]);

  useEffect(() => {
    setLoading(true);
    const req = { sort };
    if (name) req.category = name;
    if (q) req.search = q;
    http.get("/products", { params: req }).then((r) => {
      setProducts(r.data.products);
      setLoading(false);
    });
  }, [name, q, sort]);

  const subOptions = useMemo(
    () => [...new Set(products.map((p) => p.subcategory).filter(Boolean))],
    [products]
  );
  const sizeOptions = useMemo(() => {
    const vals = [...new Set(products.map((p) => p.size).filter(Boolean).map(String))];
    const allNumeric = vals.length > 0 && vals.every((v) => /^\d+(\.\d+)?$/.test(v));
    return allNumeric
      ? vals.sort((a, b) => Number(a) - Number(b))
      : SIZES.filter((s) => vals.includes(s));
  }, [products]);

  const sizeLabel = (s) => (/^\d+(\.\d+)?$/.test(String(s)) ? `${s}"` : s);

  const toggle = (list, setList, val) =>
    setList(list.includes(val) ? list.filter((v) => v !== val) : [...list, val]);

  const filtered = products.filter((p) => {
    if (subs.length && !subs.includes(p.subcategory)) return false;
    if (sizes.length && !sizes.includes(p.size)) return false;
    if (prices.length && !prices.some((i) => p.price >= PRICE_BUCKETS[i].min && p.price <= PRICE_BUCKETS[i].max))
      return false;
    return true;
  });

  const activeCount = subs.length + sizes.length + prices.length;
  const clearAll = () => { setSubs([]); setSizes([]); setPrices([]); };

  const filterPanel = (
    <div className="bg-white rounded-sm fk-shadow" data-testid="filter-sidebar">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#f0f0f0]">
        <span className="font-head font-bold text-[#212121]">Filters</span>
        {activeCount > 0 && (
          <button onClick={clearAll} className="text-xs font-semibold text-[#C62828] flex items-center gap-1" data-testid="clear-filters-btn">
            <X className="w-3.5 h-3.5" /> CLEAR ALL
          </button>
        )}
      </div>

      {subOptions.length > 0 && (
        <div className="px-4 py-3 border-b border-[#f0f0f0]">
          <p className="text-xs font-semibold text-[#878787] uppercase mb-1">Type</p>
          {subOptions.map((s) => (
            <Check
              key={s}
              label={s}
              checked={subs.includes(s)}
              onChange={() => toggle(subs, setSubs, s)}
              testid={`filter-sub-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            />
          ))}
        </div>
      )}

      <div className="px-4 py-3 border-b border-[#f0f0f0]">
        <p className="text-xs font-semibold text-[#878787] uppercase mb-1">Size</p>
        {sizeOptions.map((s) => (
          <Check
            key={s}
            label={sizeLabel(s)}
            checked={sizes.includes(s)}
            onChange={() => toggle(sizes, setSizes, s)}
            testid={`filter-size-${String(s).toLowerCase()}`}
          />
        ))}
      </div>

      <div className="px-4 py-3">
        <p className="text-xs font-semibold text-[#878787] uppercase mb-1">Price</p>
        {PRICE_BUCKETS.map((b, i) => (
          <Check
            key={b.label}
            label={b.label}
            checked={prices.includes(i)}
            onChange={() => toggle(prices, setPrices, i)}
            testid={`filter-price-${i}`}
          />
        ))}
      </div>
    </div>
  );

  return (
    <main className="max-w-[1400px] mx-auto px-2 sm:px-4 py-3" data-testid="category-page">
      {/* Mobile filter toggle */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="sm:hidden flex items-center gap-2 bg-white fk-shadow rounded-sm px-4 py-2.5 mb-3 text-sm font-semibold text-[#212121] w-full"
        data-testid="filters-toggle-btn"
      >
        <SlidersHorizontal className="w-4 h-4" /> Filters {activeCount > 0 && `(${activeCount})`}
      </button>
      {showFilters && <div className="sm:hidden mb-3">{filterPanel}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-[230px_1fr] gap-3 items-start">
        <div className="hidden sm:block sticky top-32">{filterPanel}</div>

        <div>
          <div className="bg-white rounded-sm fk-shadow mb-3 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h1 className="font-head text-lg font-bold text-[#212121]">{title}</h1>
              <p className="text-xs text-[#878787]" data-testid="listing-count">
                {filtered.length} items found
              </p>
            </div>
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
              <span className="text-sm font-semibold text-[#212121] mr-1">Sort By</span>
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSort(s.key)}
                  className={`text-sm px-3 py-1.5 whitespace-nowrap border-b-2 transition-colors ${
                    sort === s.key
                      ? "border-[#C62828] text-[#C62828] font-semibold"
                      : "border-transparent text-[#212121]"
                  }`}
                  data-testid={`sort-${s.key}`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {subOptions.length > 0 && (
              <div className="w-full flex items-center gap-2 mt-1 overflow-x-auto no-scrollbar" data-testid="subcategory-chips">
                <button
                  onClick={() => setSubs([])}
                  className={`text-sm px-4 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
                    subs.length === 0
                      ? "bg-[#C62828] text-white border-[#C62828] font-semibold"
                      : "border-[#c2c2c2] text-[#212121] hover:border-[#C62828] hover:text-[#C62828]"
                  }`}
                  data-testid="subcategory-chip-all"
                >
                  All
                </button>
                {subOptions.map((s) => {
                  const active = subs.includes(s);
                  return (
                    <button
                      key={s}
                      onClick={() => setSubs(active ? [] : [s])}
                      className={`text-sm px-4 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
                        active
                          ? "bg-[#C62828] text-white border-[#C62828] font-semibold"
                          : "border-[#c2c2c2] text-[#212121] hover:border-[#C62828] hover:text-[#C62828]"
                      }`}
                      data-testid={`subcategory-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {loading ? (
            <div className="py-20 text-center text-[#878787]">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-sm py-20 text-center text-[#878787]" data-testid="no-results">
              No products match the selected filters.
            </div>
          ) : subs.length === 0 && subOptions.length > 0 ? (
            <div className="space-y-3">
              {subOptions.map((sub) => {
                const items = filtered.filter((p) => p.subcategory === sub);
                if (items.length === 0) return null;
                const slug = sub.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                  <div key={sub} className="bg-white rounded-sm fk-shadow overflow-hidden" data-testid={`section-${slug}`}>
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#f0f0f0]">
                      <h2 className="font-head text-base font-bold text-[#212121]" data-testid={`section-title-${slug}`}>
                        {sub} <span className="text-xs font-normal text-[#878787]">({items.length})</span>
                      </h2>
                      <button
                        onClick={() => setSubs([sub])}
                        className="text-xs font-semibold text-[#C62828] hover:underline"
                        data-testid={`section-viewall-${slug}`}
                      >
                        VIEW ALL
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-px bg-[#f0f0f0]">
                      {items.map((p) => (
                        <div key={p.id} className="bg-white">
                          <ProductCard product={p} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {filtered.filter((p) => !subOptions.includes(p.subcategory)).length > 0 && (
                <div className="bg-white rounded-sm fk-shadow overflow-hidden" data-testid="section-others">
                  <div className="px-4 py-3 border-b border-[#f0f0f0]">
                    <h2 className="font-head text-base font-bold text-[#212121]">Others</h2>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-px bg-[#f0f0f0]">
                    {filtered
                      .filter((p) => !subOptions.includes(p.subcategory))
                      .map((p) => (
                        <div key={p.id} className="bg-white">
                          <ProductCard product={p} />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-px bg-[#f0f0f0] rounded-sm overflow-hidden fk-shadow">
              {filtered.map((p) => (
                <div key={p.id} className="bg-white">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default CategoryPage;
